/**
 * Permissões do usuário logado (perfil de acesso).
 * O catálogo de módulos vem do backend e é cacheado por sessão.
 */
import api from "./api";

let catalogoCache = null;

export async function carregarCatalogoPermissoes() {
  if (catalogoCache) return catalogoCache;
  const { data } = await api.get("/access-profiles/catalog");
  catalogoCache = data;
  return data;
}

/**
 * Módulo fora do plano não existe para a empresa — nem para quem tem acesso
 * total. Por isso o teto é checado ANTES do acesso total: sem isso, o admin do
 * tenant continuaria vendo módulo não contratado.
 */
export function foraDoPlano(user, chave) {
  return Boolean(chave) && (user?.blocked_keys || []).includes(chave);
}

export function caminhoForaDoPlano(user, pathname) {
  return (user?.blocked_paths || []).some(
    (p) => pathname === p || pathname.startsWith(`${p}/`),
  );
}

export function podeAgir(user, chave, acao = "view") {
  if (!user) return false;
  if (foraDoPlano(user, chave)) return false;
  if (user.full_access) return true;
  return Boolean(user.permissions?.[chave]?.[acao]);
}

/** Resolve a chave de permissão de uma rota do frontend usando o catálogo. */
export function chaveDaRota(catalogo, pathname) {
  let melhor = null;
  (catalogo?.modulos || []).forEach((mod) =>
    mod.items.forEach((item) =>
      (item.paths || []).forEach((p) => {
        if (pathname === p || pathname.startsWith(`${p}/`)) {
          if (!melhor || p.length > melhor.path.length) melhor = { path: p, key: item.key };
        }
      }),
    ),
  );
  return melhor?.key || null;
}

const ACOES_PT = {
  view: "visualizar",
  create: "cadastrar",
  edit: "editar",
  delete: "excluir",
};

/**
 * Props para botões de ação: em vez de esconder, o botão fica com cara de
 * bloqueado, não abre nada e mostra o motivo no hover.
 * `disabled` nativo mataria o mouseover e o tooltip nunca apareceria, então o
 * bloqueio é feito com aria-disabled + onClick que barra o clique.
 * IMPORTANTE: espalhar SEMPRE depois do onClick/title do componente.
 */
export function propsAcao(user, chave, acao) {
  if (podeAgir(user, chave, acao)) return {};
  const motivo = `Seu perfil de acesso não permite ${ACOES_PT[acao] || acao} aqui.`;
  return {
    "aria-disabled": true,
    "data-bloqueado": "true",
    "data-tip": motivo,
    title: motivo,
    tabIndex: -1,
    onClick: (e) => {
      e.preventDefault();
      e.stopPropagation();
    },
  };
}

/** Rótulo "Módulo › Submódulo" de uma chave (para mensagens de bloqueio). */
export function rotuloDaChave(catalogo, chave) {
  let achado = "";
  (catalogo?.modulos || []).forEach((mod) =>
    mod.items.forEach((item) => {
      if (item.key === chave) achado = `${mod.label} › ${item.label}`;
    }),
  );
  return achado;
}

/** Caminhos visíveis para o usuário — usado no filtro do menu. */
export function caminhosPermitidos(catalogo, user) {
  const set = new Set();
  (catalogo?.modulos || []).forEach((mod) =>
    mod.items.forEach((item) => {
      if (podeAgir(user, item.key, "view")) (item.paths || []).forEach((p) => set.add(p));
    }),
  );
  return set;
}

/** Página aberta após o login: a do perfil, senão a primeira liberada. */
export async function resolverPaginaInicial(user) {
  if (!user) return "/login";
  if (user.home_path) return user.home_path;
  // Equipe da plataforma sem cliente vinculado trabalha no painel admin.
  if (!user.client_id && ["support_admin", "billing_admin"].includes(user.role))
    return "/admin";
  if (user.full_access) return "/dashboard";
  try {
    const catalogo = await carregarCatalogoPermissoes();
    if (podeAgir(user, "dashboard.painel", "view")) return "/dashboard";
    for (const mod of catalogo?.modulos || []) {
      for (const item of mod.items) {
        const path = (item.paths || [])[0];
        if (path && podeAgir(user, item.key, "view")) return path;
      }
    }
  } catch {
    /* segue com o padrão */
  }
  return "/dashboard";
}
